<template>
  <v-container
    fluid
    class="ac2-page">
    <v-row>
      <v-col
        cols="12"
        :md="selectItem && showDetail ? '8' : '12'">
        <v-card
          class="rounded-lg table-card">
          <v-data-table
            :headers="header"
            :items="items"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            :server-items-length="total"
            :loading="loading"
            hide-default-footer
            must-sort
            class="w-full"
            @update:options="onOptionsUpdate"
          >
            <template v-slot:top>
              <div class="d-flex align-center flex-wrap px-4 pt-4 pb-2">
                <h6 class="text-h6 mb-0 mr-4">
                  AC2 Queues
                </h6>
                <v-spacer />
                <v-select
                  v-model="filterStatus"
                  :items="status"
                  item-text="text"
                  item-value="value"
                  label="สถานะ"
                  color="secondary"
                  class="flex-grow-0 mr-3"
                  style="max-width: 180px;"
                  outlined
                  dense
                  hide-details>
                </v-select>
                <search-box
                  v-model="inputSearch"
                  class="flex-grow-0"
                  style="max-width: 260px;"
                  @on-search="getData(true)" />
              </div>
              <v-divider />
            </template>
            <template #[`item.pending`]="{ item }">
              <div class="text-center">
                <v-btn
                  link
                  icon
                  x-small
                  :disabled="(item.status === 'pending' || item.status === 'running')"
                  @click="forcePending(item.id)">
                  <v-icon>
                    mdi-human-queue
                  </v-icon>
                </v-btn>
              </div>
            </template>

            <template #[`item.updatedAt`]="{ item }">
              <span style="white-space: nowrap;">
                {{ item.updatedAt | dateTimeUserFormat() }}
              </span>
            </template>
            <template #[`item.createdAt`]="{ item }">
              <span style="white-space: nowrap;">
                {{ item.createdAt | dateTimeUserFormat() }}
              </span>
            </template>
            <template #[`item.description`]="{ item }">
              <div style="display: flex; flex-direction: row; align-items: center; gap: 0.2rem;">
                <v-icon :color="getStatusStyle(item.status).color">
                  {{ getStatusStyle(item.status).icon }}
                </v-icon>
                <span class="limit-text">
                  {{ item.description }}
                </span>
              </div>
            </template>
            <template #[`item.option`]="{ item }">
              <v-btn
                link
                icon
                x-small
                :disabled="isShowButton(item) && (item.status === 'forcing' || item.status === 'running')"
                @click="onConfirmForceRun(item)">
                <v-icon>
                  mdi-play-outline
                </v-icon>
              </v-btn>
              <v-btn
                link
                icon
                x-small
                :disabled="isShowButton(item)"
                @click="editItem(item)">
                <v-icon>
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
              <v-btn
                link
                icon
                x-small
                :disabled="showDetail && selectItem && selectItem.id === item.id"
                @click="showItemDetail(item)">
                <v-icon>
                  mdi-magnify
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-divider />
          <div class="d-flex align-center flex-wrap px-4 py-3">
            <span class="text-caption grey--text">
              Total {{ total }} items
            </span>
            <v-spacer />
            <v-pagination
              v-model="options.page"
              :length="pageCount"
              :total-visible="5"
              color="secondary"
              @input="getData()" />
          </div>
        </v-card>
      </v-col>
      <v-col
        v-if="selectItem && showDetail"
        cols="12"
        md="4">
        <v-card class="rounded-lg detail-card pa-4">
          <div style="display: flex; flex-direction: row; align-items: center; gap: 0.2rem;">
            <v-icon :color="getStatusStyle(selectItem.status).color">
              {{ getStatusStyle(selectItem.status).icon }}
            </v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mb-0">
              {{ selectItem.description }}
            </h3>
          </div>
          <p class="mb-0 mt-2">
            <span
              class="text-bold">{{ selectItem.httpStatus }}</span> {{ selectItem.statusText }}
          </p>
          <p class="mb-0">
            <b>Date </b>{{ selectItem.createdAt | dateTimeUserFormat() }}
          </p>
          <p class="mb-0">
            <b>Last update </b>{{ selectItem.updatedAt | dateTimeUserFormat() }}
          </p>
          <p class="mb-0">
            <b>Document No. </b> {{ selectItem.documentNo }}
          </p>
          <p class="mb-0">
            <b>Status </b> {{ selectItem.status.replaceAll('_', ' ') }}
          </p>
          <p class="mb-0">
            <b>Note </b> {{ selectItem.note }}
          </p>
          <div class="mt-4">
            <a @click="scrollTo('Info')">#Info</a>
            <a
              class="ml-4"
              @click="scrollTo('Payload')">#Request Body</a>
            <a
              class="ml-4"
              @click="scrollTo('Response')">#Response</a>
          </div>
          <p
            ref="Info"
            class="mt-4 mb-1 font-weight-medium">
            Info
          </p>
          <div class="json-box">
            <json-viewer
              :value="{
                functionName: selectItem.functionName,
                owner: selectItem.owner,
                whId: selectItem.whId,
                extIfSource: selectItem.extIfSource,
                extIfOrigin: selectItem.extIfOrigin,
                docCollection: selectItem.docCollection,
                docId: selectItem.docId,
                docType: selectItem.docType,
                ac2Log: selectItem.ac2Log,
                httpStatus: selectItem.httpStatus,
                statusText: selectItem.statusText,
                responseTime: selectItem.responseTime,
                errorCount: selectItem.errorCount,
                isNotify: selectItem.isNotify
              }"
              :expand-depth="1"
              copyable />
          </div>
          <p
            ref="Payload"
            class="mt-4 mb-1 font-weight-medium">
            Request Body
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.requestBody"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            ref="Response"
            class="mt-4 mb-1 font-weight-medium">
            Response
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.result"
              :expand-depth="5"
              copyable
              sort />
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog
      v-model="showEdit"
      persistent
      max-width="400px">
      <v-card>
        <v-card-title>
          <span class="text-h5">AC2 Queue Form</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="ac2-queue-form"
              @submit.prevent="onSubmit()">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="formData.status"
                    :items="status"
                    item-text="text"
                    item-value="value"
                    color="secondary"
                    label="สถานะ"
                    outlined
                    dense
                    hide-details>
                  </v-select>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="red darken-1"
            text
            :loading="loading"
            @click="closeForm()">
            Close
          </v-btn>
          <v-btn
            type="submit"
            color="green darken-1"
            text
            :loading="loading"
            @click="onSubmit()">
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import SearchBox from '@/components/SearchBox.vue'
import Ac2QueuesProvider from '@/resources/Ac2QueuesProvider'

const Ac2QueuesService = new Ac2QueuesProvider()

export default {
  components: {
    SearchBox
  },
  data () {
    return {
      inputSearch: '',
      filterStatus: null,
      status: [
        {
          text: 'All',
          value: null,
          color: 'warning',
          icon: 'mdi-help-circle-outline'
        },
        {
          text: 'Pending',
          value: 'pending',
          color: '#bbbbbb',
          icon: 'mdi-clock-outline'
        },
        {
          text: 'Waiting Stock',
          value: 'waiting_stock',
          color: '#ff9800',
          icon: 'mdi-package-variant-closed'
        },
        {
          text: 'Running',
          value: 'running',
          color: 'warning',
          icon: 'mdi-sync'
        },
        {
          text: 'Forcing',
          value: 'forcing',
          color: 'warning',
          icon: 'mdi-sync'
        },
        {
          text: 'Success',
          value: 'success',
          color: 'success',
          icon: 'mdi-check'
        },
        {
          text: 'Failed',
          value: 'failed',
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        },
        {
          text: 'Skipped',
          value: 'skipped',
          color: '#9b23c3',
          icon: 'mdi-debug-step-over'
        },
        {
          text: 'Cancel Before Sync',
          value: 'cancel_before_sync',
          color: 'error',
          icon: 'mdi-sync-off'
        },
        {
          text: 'Deleted',
          value: 'deleted',
          color: 'error',
          icon: 'mdi-delete-outline'
        }
      ],
      loading: false,
      selectItem: null,
      formData: {
        id: null,
        status: 'pending'
      },
      showDetail: false,
      showEdit: false,
      total: 0,
      totalPages: 0,
      sortSnapshot: null,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createdAt'],
        sortDesc: [false],
        status: null
      },
      header: [
        {
          text: 'Queue',
          value: 'pending',
          width: '90px'
        },
        {
          text: 'Last update',
          value: 'updatedAt'
        },
        {
          text: 'Date',
          value: 'createdAt'
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false
        },
        {
          text: 'Document No',
          value: 'documentNo',
          sortable: false
        },
        {
          text: 'Options',
          value: 'option',
          align: 'center',
          sortable: false,
          width: '100px'
        }
      ],
      items: []
    }
  },
  computed: {
    pageCount () {
      return this.totalPages || 1
    }
  },
  watch: {
    filterStatus: {
      handler (val) {
        this.options.sortDesc = [val === 'success']
        this.sortSnapshot = this.getSortKey()
        this.getData(true)
      },
      deep: true
    }
  },
  created () {
    this.getData(true)
  },
  methods: {
    ...mapActions({
      setModal: 'Components/setModal'
    }),
    getSortKey () {
      return `${this.options.sortBy.join(',')}|${this.options.sortDesc.join(',')}`
    },
    onOptionsUpdate () {
      const sortKey = this.getSortKey()
      if (this.sortSnapshot === null) {
        this.sortSnapshot = sortKey
        return
      }
      if (sortKey !== this.sortSnapshot) {
        this.sortSnapshot = sortKey
        this.getData(true)
      }
    },
    showItemDetail (item) {
      this.selectItem = item
      this.showDetail = true
      this.showEdit = false
    },
    onConfirmForceRun (item) {
      this.setModal({
        value: true,
        title: 'Force Run',
        message: 'Do you want to force run this document',
        confirmText: 'Sure',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.forceRun(item)
      })
    },
    async forceRun (item) {
      try {
        this.loading = true

        await Ac2QueuesService.forceRun(item.id)

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Force run queue success.',
          type: 'success'
        })

        this.getData(true)
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    editItem (item) {
      this.selectItem = item
      this.formData = { ...item }
      this.showDetail = false
      this.showEdit = true
    },
    closeForm () {
      this.selectItem = null
      this.showEdit = false
    },
    scrollTo (tag) {
      const el = this.$refs[tag]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    getStatusStyle (status) {
      const found = this.status.find((s) => s.value === status)

      return {
        color: found?.color || 'warning',
        icon: found?.icon || 'mdi-help-circle-outline'
      }
    },
    async getData (clear = false) {
      this.showDetail = false

      if (clear) {
        this.options.page = 1
      }

      try {
        this.loading = true
        const { data: { results, totalDocs, totalPages } } = await Ac2QueuesService.getAc2Queues(
          {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            sortBy: this.options.sortBy[0],
            sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
            status: this.filterStatus,
            search: this.inputSearch
          }
        )
        this.items = results
        this.total = totalDocs
        this.totalPages = totalPages
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async forcePending (id) {
      try {
        this.loading = true

        await Ac2QueuesService.updateAc2QueueById({
          id,
          status: 'pending'
        })

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Update to pending success.',
          type: 'success'
        })

        this.closeForm()
        this.getData(true)
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async onSubmit () {
      try {
        this.loading = true

        await Ac2QueuesService.updateAc2QueueById(this.formData)

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Update queue success.',
          type: 'success'
        })

        this.closeForm()
        this.getData(true)
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    isShowButton (item) {
      return this.showEdit && this.selectItem && this.selectItem.id === item.id
    }
  }
}
</script>
<style scoped>
.table-card,
.detail-card {
  height: calc(100vh - 96px);
  overflow-y: auto;
}
.json-box {
  border: thin solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}
.theme--dark .json-box {
  border-color: rgba(255, 255, 255, 0.12);
}
</style>
